/* eslint-disable react/jsx-props-no-spreading -- the post panel takes the post handlers of the screen */
import React, { useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import type { XPostsUsage } from 'fym-shared/src/music24/xPosts';
import { TypedMemo } from '../../../common/types';
import type { CalendarPost, CalendarSong } from '../types';
import { AddOnDayMenu } from './AddOnDayMenu';
import { AgendaRow } from './AgendaRow';
import type { CalendarDay } from './calendarDates';
import type { CalendarDayContent } from './calendarDayModel';
import type { CalendarFreeSlot } from './calendarFreeSlots';
import { DayPanelPost } from './DayPanelPost';
import type { PostPanelHandlers } from './dayPanelTypes';
import { ReleaseBand } from './ReleaseBand';
import { Panel } from '../../components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the panel; each part uses its own slice of it */
import styles from './DayPanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface DayPanelProps extends PostPanelHandlers {
  day: CalendarDay;
  content: CalendarDayContent;
  songs: Map<string, CalendarSong>;
  /** The open post. Null: the first post of the day. */
  selectedPost: CalendarPost | null;
  slots: CalendarFreeSlot[];
  onSelect: (post: CalendarPost) => void;
  onClose: () => void;
  onAdd: (day: Date, slot: CalendarFreeSlot | null) => void;
  /** The X posts of this month of the X channel, or null. */
  xPosts?: XPostsUsage | null;
}

/** The most drafts that one "Schedule all" request takes (`POST promo/posts/schedule`). */
const SCHEDULE_DRAFTS_MAX = 50;

/**
 * The day panel (design b1), in place of the old modal: the grid stays visible. The posts of the
 * day on top, the open post below with the actions of its state only. Escape closes it.
 */
const DayPanelComponent: React.FC<DayPanelProps> = ({
  day,
  content,
  songs,
  selectedPost,
  slots,
  onSelect,
  onClose,
  onAdd,
  xPosts = null,
  ...handlers
}) => {
  const intl = useIntl();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const post = selectedPost ?? content.posts[0] ?? null;
  // "Schedule all" takes the drafts whose time is still ahead, at most 50. A draft that needs the
  // user's settings (TikTok, YouTube) is in the list: the launch asks for them one time and never
  // arms a post without them (TikTok Direct Post G10).
  const dayDrafts = useMemo(() => {
    const now = Date.now();
    return content.posts
      .filter((one) => one.status === 'draft' && new Date(one.scheduledAt).getTime() > now)
      .slice(0, SCHEDULE_DRAFTS_MAX);
  }, [content.posts]);
  const songOf = (one: CalendarPost) => (one.catalogItemUuid === null ? undefined : songs.get(one.catalogItemUuid));
  const fullDate = intl.formatDate(day.start, { weekday: 'long', day: 'numeric', month: 'long' });

  useEffect(() => {
    headingRef.current?.focus();
  }, [day.key]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const eyebrow = [
    day.isToday ? intl.formatMessage({ id: 'app.sml.promote.calendar.list.today', defaultMessage: 'Today' }) : null,
    content.releases.length > 0
      ? intl.formatMessage({ id: 'app.sml.promote.calendar.panel.releaseDay', defaultMessage: 'Release day' })
      : null,
    intl.formatMessage(
      { id: 'app.sml.promote.calendar.panel.count', defaultMessage: '{count, plural, one {# post} other {# posts}}' },
      { count: content.posts.length }
    ),
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');

  return (
    <Panel as="aside" className={styles.dpanel} aria-label={fullDate} data-testid="calendar-day-panel">
      <div className={styles.dpHead}>
        <div className={styles.grow}>
          <div className={styles.eb}>{eyebrow}</div>
          <h3 ref={headingRef} tabIndex={-1}>
            {fullDate}
          </h3>
        </div>
        {day.isPast ? null : <AddOnDayMenu day={day.start} slots={slots} onPick={onAdd} isAlwaysVisible />}
        <button
          type="button"
          className={styles.xbtn}
          aria-label={intl.formatMessage({ id: 'app.sml.promote.calendar.panel.close', defaultMessage: 'Close' })}
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
      {content.releases.map((song) => (
        <ReleaseBand key={song.catalogItemUuid} song={song} isToday={day.isToday} />
      ))}
      {content.posts.length > 1 ? (
        <div className={styles.dpList}>
          {content.posts.map((one) => (
            <AgendaRow
              key={one.uuid}
              post={one}
              song={songOf(one)}
              isSelected={post?.uuid === one.uuid}
              onOpen={onSelect}
            />
          ))}
        </div>
      ) : null}
      {post === null ? (
        <p className={styles.empty}>
          {intl.formatMessage({ id: 'app.sml.promote.calendar.panel.empty', defaultMessage: 'No post on this day.' })}
        </p>
      ) : (
        <DayPanelPost post={post} song={songOf(post)} dayDrafts={dayDrafts} xPosts={xPosts} {...handlers} />
      )}
    </Panel>
  );
};

export const DayPanel = TypedMemo(DayPanelComponent);
